import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getFavorites, getLocalFavorites } from '../services/favorites';
import PropertyCard from '../components/PropertyCard';
import { FiHeart } from 'react-icons/fi';
import api from '../services/api';
import './Favorites.css';

const Favorites = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFavorites();
  }, [isAuthenticated]);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isAuthenticated) {
        // User is authenticated - fetch from backend
        const result = await getFavorites();
        const favs = result?.favorites || [];
        setFavorites(favs);
      } else {
        // User is not authenticated - get from localStorage
        const localFavIds = getLocalFavorites();

        if (!localFavIds || localFavIds.length === 0) {
          setFavorites([]);
          return;
        }

        // Fetch property details for local favorites
        const propertyPromises = localFavIds.map(id =>
          api.get(`/api/properties/${id}`)
            .then(res => res.data?.property || null)
            .catch(err => {
              console.error(`Failed to fetch property ${id}:`, err);
              return null;
            })
        );

        const properties = await Promise.all(propertyPromises);
        setFavorites(properties.filter(p => p !== null));
      }
    } catch (err) {
      console.error('Error fetching favorites:', err);
      setError(t('favorites.errorLoading') || 'Failed to load favorites');
      // Ensure favorites is always an array even on error
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="favorites-page">
        <div className="container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{t('common.loading') || 'Loading...'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="favorites-page">
        <div className="container">
          <div className="error-message">
            <p>{error}</p>
            <button onClick={fetchFavorites} className="btn btn-primary">
              {t('common.tryAgain') || 'Try Again'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="favorites-page">
      <div className="container">
        <div className="favorites-header">
          <h1>
            <FiHeart className="heart-icon" />
            {t('favorites.title') || 'My Favorites'}
          </h1>
          <p className="favorites-count">
            {favorites?.length || 0} {(favorites?.length || 0) === 1
              ? (t('favorites.property') || 'property')
              : (t('favorites.properties') || 'properties')}
          </p>
        </div>

        {!isAuthenticated && (favorites?.length || 0) > 0 && (
          <div className="login-prompt">
            <p>
              {t('favorites.loginPrompt') || 'Sign in to save your favorites across all your devices'}
            </p>
            <button
              onClick={() => navigate('/login')}
              className="btn btn-primary"
            >
              {t('navbar.login') || 'Login'}
            </button>
          </div>
        )}

        {!favorites || favorites.length === 0 ? (
          <div className="empty-state">
            <FiHeart className="empty-icon" />
            <h2>{t('favorites.empty.title') || 'No favorites yet'}</h2>
            <p>
              {t('favorites.empty.description') ||
                'Start exploring and save your favorite properties by clicking the heart icon'}
            </p>
            <button
              onClick={() => navigate('/properties')}
              className="btn btn-primary"
            >
              {t('navbar.explore') || 'Explore Properties'}
            </button>
          </div>
        ) : (
          <div className="favorites-grid">
            {favorites?.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                showHost={true}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Favorites;
